import { AgentSelect } from '@/components/AgentSelect';
import { LoadingState } from '@/components/StateMessage';
import {
  useGetChatAgentsQuery,
  useGetChatConversationsQuery,
} from '@/features/agents/agentsApi';
import { chatAppLabel } from '@/features/agents/chatApps';
import { useAgentChat } from '@/features/agents/useAgentChat';
import { useRetryOnUnauthorized } from '@/features/agents/useRetryOnUnauthorized';
import { useSession } from '@/features/session/SessionProvider';
import { SessionGate } from '@/features/session/components/SessionGate';
import { PanelBody } from '@/pages/Embed/components/PanelBody';
import { PanelHeader } from '@/pages/Embed/components/PanelHeader';
import { PanelMenu } from '@/pages/Embed/components/PanelMenu';
import { useEmbedHost } from '@/pages/Embed/useEmbedHost';

import { Stack } from '@mui/joy';

/** The latest conversations of the panel's app fit the «⋯» menu; the full page lists them all. */
const HISTORY_PAGE_SIZE = 20;

/**
 * `/embed?app=<key>` — the «Gasbi AI» panel the launcher frames in an employee app (ADR-0004 §2.1, §2.7, §2.8):
 * the agents of that app, the conversations started in it, a new one started in it. The header stays while the
 * session gate below it signs in; the chat waits for the host app's `init` (see `useEmbedHost`).
 */
export const EmbedPage = () => {
  const host = useEmbedHost();
  const { state } = useSession();
  useRetryOnUnauthorized();

  const enabled = host.ready && state.status === 'signedIn';
  const appKey = host.app || undefined;
  const appLabel = chatAppLabel(appKey);
  const agentsQuery = useGetChatAgentsQuery(appKey, { skip: !enabled });
  const historyQuery = useGetChatConversationsQuery(
    { app: appKey, page: 1, pageSize: HISTORY_PAGE_SIZE },
    { skip: !enabled },
  );
  const agents = agentsQuery.data ?? [];
  const chat = useAgentChat({
    agents,
    appKey,
    enabled,
    onAnswered: host.notifyUnread,
  });
  const showChat = enabled && agents.length > 0;

  return (
    <Stack sx={{ height: '100dvh', bgcolor: 'background.surface' }}>
      <PanelHeader
        subtitle={
          showChat ? (
            <AgentSelect
              compact
              agents={agents}
              value={chat.agent?.id}
              onChange={chat.selectAgent}
            />
          ) : (
            (appLabel ?? '')
          )
        }
        actions={
          showChat && (
            <PanelMenu
              appLabel={appLabel}
              history={historyQuery.data?.items ?? []}
              selectedId={chat.conversationId}
              onNew={() => chat.open(null)}
              onOpen={chat.open}
            />
          )
        }
        onClose={host.framed ? host.close : undefined}
      />
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <SessionGate>
          {host.ready ? (
            <PanelBody
              chat={chat}
              appLabel={appLabel}
              agentsCount={agents.length}
              isLoading={agentsQuery.isLoading}
              isError={agentsQuery.isError}
              onRetry={agentsQuery.refetch}
            />
          ) : (
            <LoadingState />
          )}
        </SessionGate>
      </Stack>
    </Stack>
  );
};
